import {
  sendGTMEventAuth,
  sendGTMEventAuthAlternativeSwitch,
  sendGTMEventAuthCancel,
  sendGTMEventAuthCodeSent,
  sendGTMEventAuthCodeSubmit,
  sendGTMEventAuthOpen,
  sendGTMEventAuthStart,
  sendGTMEventBodyButtonClick,
  sendGTMEventEcommerce,
  sendGTMEventInternalSearch,
  sendGTMEventMenuClick,
  sendGTMEventPromotion,
  sendGTMEventStoriesButtonClick,
  sendGTMEventStoriesClick,
  sendGTMEventStoriesView,
  sendGTMEventUseFilters,
  sendGTMEventMenuFilter,
  sendGTMEventNewsletterRegistration,
  sendGTMEventAddContactInfo,
  sendGTMEventApplyGiftCard,
  sendGTMEventScrollGallery,
  sendGTMEventReadReview,
  sendGTMEventWriteReview,
  sendGTMEventSubmitReview,
  sendGTMEventSocialInteraction
} from './events';
import {
  GTMAuthContext,
  GTMAuthMethod,
  GTMAuthOpenLabel,
  GTMEventAuth,
  GTMEventEcommerce,
  GTMEventNameEcommerce,
  GTMEventPromotion,
  GTMPurchaseDiscountSource
} from './types';

type GTMEcommercePayload<T extends GTMEventNameEcommerce> = {
  ecommerce: GTMEventEcommerce<T>['ecommerce'];
};
type GTMAuthPayload = Pick<GTMEventAuth, 'placement' | 'method'>;
type GTMPromotionPayload = {
  ecommerce: GTMEventPromotion['ecommerce'];
};

export const apiGtm = {
  ecommerce: sendGTMEventEcommerce,
  promotion: sendGTMEventPromotion,
  purchase: (
    payload: GTMEcommercePayload<'purchase'> & {
      discount_source: GTMPurchaseDiscountSource;
    }
  ) => sendGTMEventEcommerce({ event: 'purchase', ...payload }),
  viewItemList: (payload: GTMEcommercePayload<'view_item_list'>) =>
    sendGTMEventEcommerce({ event: 'view_item_list', ...payload }),
  selectItem: (payload: GTMEcommercePayload<'select_item'>) =>
    sendGTMEventEcommerce({ event: 'select_item', ...payload }),
  addToCart: (payload: GTMEcommercePayload<'add_to_cart'>) =>
    sendGTMEventEcommerce({ event: 'add_to_cart', ...payload }),
  removeFromCart: (payload: GTMEcommercePayload<'remove_from_cart'>) =>
    sendGTMEventEcommerce({ event: 'remove_from_cart', ...payload }),
  addToWishlist: (payload: GTMEcommercePayload<'add_to_wishlist'>) =>
    sendGTMEventEcommerce({ event: 'add_to_wishlist', ...payload }),
  removeFromWishlist: (payload: GTMEcommercePayload<'remove_from_wishlist'>) =>
    sendGTMEventEcommerce({ event: 'remove_from_wishlist', ...payload }),
  addPaymentInfo: (payload: GTMEcommercePayload<'add_payment_info'>) =>
    sendGTMEventEcommerce({ event: 'add_payment_info', ...payload }),
  addShippingInfo: (payload: GTMEcommercePayload<'add_shipping_info'>) =>
    sendGTMEventEcommerce({ event: 'add_shipping_info', ...payload }),
  signUp: (payload: GTMAuthPayload) => sendGTMEventAuth({ event: 'sign_up', ...payload }),
  login: (payload: GTMAuthPayload) => sendGTMEventAuth({ event: 'login', ...payload }),
  authOpen: (payload: { event_label: GTMAuthOpenLabel; auth_context: GTMAuthContext }) =>
    sendGTMEventAuthOpen(payload),
  authStart: (payload: {
    auth_session_id: string;
    auth_method: GTMAuthMethod;
    auth_context: GTMAuthContext;
  }) => sendGTMEventAuthStart(payload),
  authCodeSent: (payload: {
    auth_session_id: string;
    auth_method: GTMAuthMethod;
    provider?: GTMAuthMethod;
  }) => sendGTMEventAuthCodeSent(payload),
  authCodeSubmit: (payload: {
    auth_session_id: string;
    auth_method: GTMAuthMethod;
    attempt_number: number;
  }) => sendGTMEventAuthCodeSubmit(payload),
  authCancel: (payload: { auth_session_id: string; auth_context: GTMAuthContext }) =>
    sendGTMEventAuthCancel(payload),
  authAlternativeSwitch: (payload: {
    auth_session_id: string;
    auth_context: GTMAuthContext;
  }) => sendGTMEventAuthAlternativeSwitch(payload),
  internalSearch: sendGTMEventInternalSearch,
  menuClick: sendGTMEventMenuClick,
  storiesClick: sendGTMEventStoriesClick,
  storiesView: sendGTMEventStoriesView,
  storiesButtonClick: sendGTMEventStoriesButtonClick,
  useFilters: sendGTMEventUseFilters,
  menuFilter: sendGTMEventMenuFilter,
  newsletterRegistration: sendGTMEventNewsletterRegistration,
  addContactInfo: sendGTMEventAddContactInfo,
  applyGiftCard: sendGTMEventApplyGiftCard,
  scrollGallery: sendGTMEventScrollGallery,
  readReview: sendGTMEventReadReview,
  writeReview: sendGTMEventWriteReview,
  submitReview: sendGTMEventSubmitReview,
  socialInteraction: sendGTMEventSocialInteraction,
  bodyButtonClick: sendGTMEventBodyButtonClick,
  viewPromotion: (payload: GTMPromotionPayload) =>
    sendGTMEventPromotion({ event: 'view_promotion', ...payload }),
  selectPromotion: (payload: GTMPromotionPayload) =>
    sendGTMEventPromotion({ event: 'select_promotion', ...payload })
};
